import { Treemap, ResponsiveContainer, Tooltip } from "recharts";
import styles from "./TreeMap.module.scss";
import { useMemo } from "react";

interface TreeMapProps {
  data: any;
}

/**
 * CustomContent is a render function for the Treemap component.
 * It defines how each node in the treemap is drawn, including rectangle styles,
 * dynamic coloring based on index, and text labels for name and value.
 *
 * @function
 * @param {Object} props - Props passed from Recharts to render each treemap node.
 * @param {Object} props.root - The root node of the treemap data structure.
 * @param {number} props.depth - Depth level of the current node.
 * @param {number} props.x - X coordinate for node position.
 * @param {number} props.y - Y coordinate for node position.
 * @param {number} props.width - Width of the node rectangle.
 * @param {number} props.height - Height of the node rectangle.
 * @param {number} props.index - Index of the node among siblings.
 * @param {Array} props.colors - Array of color objects to apply to nodes.
 * @param {string} props.name - Label of the data item.
 * @param {number} props.value - Value of the data item.
 *
 * @returns {JSX.Element} The rendered SVG group (`<g>`) with a rectangle and optional labels.
 */
const CustomContent = (props: any) => {
  const { root, depth, x, y, width, height, index, colors, name, value } =
    props;

  const colorIndex = Math.floor((index / (root.children?.length || 1)) * 6);
  const backgroundColor = colors[colorIndex]?.background || "#ccc";
  const textColor = colors[colorIndex]?.color ?? "white";

  return (
    <g transform={`translate(${x},${y})`}>
      <rect
        width={width}
        height={height}
        className={`${styles.treemapRect} ${
          depth >= 2 ? styles.treemapRectLight : ""
        }`}
        fill={depth < 2 ? backgroundColor : undefined}
      />
      {height < 50 ? (
        <text
          x={8}
          y={24}
          className={styles.labelPrimary}
          fill={textColor}
          stroke={textColor}
        >
          {name} {value}
        </text>
      ) : (
        <>
          <text
            x={16}
            y={32}
            className={styles.labelPrimary}
            fill={textColor}
            stroke={textColor}
          >
            {name}
          </text>

          <text
            x={16}
            y={height > 60 ? 62 : 46}
            className={height > 60 ? styles.labelValue : styles.labelValueSmall}
            fill={textColor}
            stroke={textColor}
          >
            {value}
          </text>
        </>
      )}
    </g>
  );
};

/**
 * TreeMap component renders a responsive treemap chart using Recharts.
 * It visualizes hierarchical data using nested rectangles, and uses a custom content renderer
 * to style nodes with labels, values, and color coding.
 *
 * @component
 * @param {TreeMapProps} props
 * @param {any} props.data - The hierarchical data structure used to render the treemap.
 *
 * @example
 * const data = [
 *   { name: "Group A", value: 100 },
 *   { name: "Group B", value: 200 },
 * ];
 *
 * <TreeMap data={data} />
 */
const TreeMap = ({ data }: TreeMapProps) => {
  const COLORS = [
    { background: "#1C359A" },
    { background: "#455EC3" },
    { background: "#6E81D0" },
    { background: "#5081E6" },
    { background: "#10225B" },
    { background: "#DADFF2", color: "#6E81D0" },
    { background: "#CCE3F7", color: "#6E81D0" },
  ];

  const dataToShow = useMemo(() => {
    return data
      ? data.sort((a: any, b: any) => b.value - a.value).slice(0, 10)
      : [];
  }, [data]);

  return (
    <ResponsiveContainer
      width="100%"
      height={dataToShow?.length > 8 ? 450 : 250}
    >
      <Treemap
        width={730}
        height={dataToShow?.length > 8 ? 450 : 250}
        data={dataToShow}
        dataKey="value"
        stroke="#fff"
        content={<CustomContent colors={COLORS} />}
        aspectRatio={1.0}
      >
        <Tooltip
          cursor={{ fill: "#626262" }}
          formatter={(value, name, { payload }) => [`${value}`, payload.name]}
        />
      </Treemap>
    </ResponsiveContainer>
  );
};

export default TreeMap;
